/**
 * ResizableImage - TipTap Image node with a width/height drag handle.
 *
 * Mirrors how both Freshdesk and Gemini already encode inline images in
 * historical tickets - the resize is persisted as ``width`` and
 * ``height`` attributes on the ``<img>`` element, which means a reply
 * email rendered in any mail client respects the agent's sizing
 * without needing CSS to ride along. Pixel values only; we do not emit
 * percent widths because Outlook ignores them.
 *
 * The corner handle is bottom-right only (the common case) and resizes
 * proportionally. Drag operates in pixel space, computed against the
 * editor's bounding rect at mousedown. There is no minimum height -
 * the image's natural aspect ratio drives height while the user drags
 * width.
 */
import { mergeAttributes } from "@tiptap/core";
import Image from "@tiptap/extension-image";
import {
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type NodeViewProps,
} from "@tiptap/react";
import { useEffect, useRef, useState } from "react";

const MIN_WIDTH_PX = 32;
const MAX_WIDTH_PX = 1600;

function ResizableImageNodeView({
  node,
  updateAttributes,
  selected,
  editor,
}: NodeViewProps): JSX.Element {
  const src = node.attrs.src as string | undefined;
  const alt = (node.attrs.alt as string | undefined) ?? "";
  const title = node.attrs.title as string | undefined;
  const width = node.attrs.width as number | string | null | undefined;
  const height = node.attrs.height as number | string | null | undefined;

  const imgRef = useRef<HTMLImageElement | null>(null);
  const [dragState, setDragState] = useState<null | {
    startX: number;
    startWidth: number;
    aspect: number;
  }>(null);

  // While the user drags, attach window-level listeners so the drag
  // continues even if the cursor strays off the handle.
  useEffect(() => {
    if (!dragState) return;

    const onMove = (ev: MouseEvent) => {
      const delta = ev.clientX - dragState.startX;
      const nextWidth = Math.max(
        MIN_WIDTH_PX,
        Math.min(MAX_WIDTH_PX, Math.round(dragState.startWidth + delta)),
      );
      const nextHeight = Math.max(1, Math.round(nextWidth / dragState.aspect));
      updateAttributes({ width: nextWidth, height: nextHeight });
    };
    const onUp = () => setDragState(null);

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp, { once: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragState, updateAttributes]);

  const onHandleMouseDown = (ev: React.MouseEvent) => {
    if (!editor.isEditable) return;
    ev.preventDefault();
    ev.stopPropagation();
    const img = imgRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    const aspect = rect.height > 0 ? rect.width / rect.height : 1;
    setDragState({
      startX: ev.clientX,
      startWidth: rect.width,
      aspect,
    });
  };

  const widthStyle =
    typeof width === "number"
      ? `${width}px`
      : typeof width === "string" && width !== ""
        ? width
        : undefined;
  const heightStyle =
    typeof height === "number"
      ? `${height}px`
      : typeof height === "string" && height !== ""
        ? height
        : undefined;

  return (
    <NodeViewWrapper
      as="span"
      className="relative inline-block leading-none"
      data-resizable-image=""
    >
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        title={title}
        width={typeof width === "number" ? width : undefined}
        height={typeof height === "number" ? height : undefined}
        draggable={false}
        style={{
          width: widthStyle,
          height: heightStyle,
          maxWidth: "100%",
          display: "inline-block",
          outline:
            selected || dragState
              ? "2px solid rgb(37 99 235)" // tailwind primary-600
              : undefined,
          outlineOffset: "2px",
          cursor: editor.isEditable ? "default" : "auto",
        }}
      />
      {(selected || dragState) && editor.isEditable && (
        <span
          role="presentation"
          aria-label="Resize image"
          onMouseDown={onHandleMouseDown}
          className="absolute bottom-0 right-0 z-10 h-3 w-3 cursor-nwse-resize bg-primary-600 ring-2 ring-white"
          style={{ transform: "translate(50%, 50%)" }}
        />
      )}
    </NodeViewWrapper>
  );
}

/**
 * Extended Image node with width/height attrs + a resize handle NodeView.
 * The serialized HTML is a plain ``<img src alt width height>`` so it
 * renders identically in email clients and in the read-only view path.
 */
export const ResizableImage = Image.extend({
  name: "image",

  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (el) => {
          const raw = el.getAttribute("width");
          if (!raw) return null;
          const n = parseInt(raw, 10);
          return Number.isFinite(n) ? n : raw;
        },
        renderHTML: (attrs) =>
          attrs.width == null ? {} : { width: String(attrs.width) },
      },
      height: {
        default: null,
        parseHTML: (el) => {
          const raw = el.getAttribute("height");
          if (!raw) return null;
          const n = parseInt(raw, 10);
          return Number.isFinite(n) ? n : raw;
        },
        renderHTML: (attrs) =>
          attrs.height == null ? {} : { height: String(attrs.height) },
      },
    };
  },

  renderHTML({ HTMLAttributes }) {
    return ["img", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageNodeView);
  },
});

export default ResizableImage;
